import AttachEmailOutlinedIcon from '@mui/icons-material/AttachEmailOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PersonIcon from '@mui/icons-material/Person';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import { Timeline, TimelineItem } from '@mui/lab';
import {
  Avatar,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Stack,
  ThemeOptions,
  Box,
  Typography,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { HiOutlineBriefcase, HiOutlineEye } from 'react-icons/hi';
import {
  MdMailOutline,
  MdOutlineCampaign,
  MdOutlineLocationOn,
  MdOutlinePhone,
  MdOutlineScreenSearchDesktop,
} from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  getCommunicationAccountsForRecruiter,
  getGoogleCalendarAccountsForRecruiter,
  getJobBoardByJBName,
  getJobBoardsByName,
  getOutlookCalendarAccountsForRecruiter,
  getParentList,
  getSMTPAccountsByRecruiter,
  getUserById,
  getWorkspaceUserById,
  ICalendarDTO,
  ICommunicationServiceAccount,
  IInviteStatus,
  IJobBoard,
  ISMTPAccountsResponse,
  IWorkspace,
  IWorkspacePaymentInfo,
  getWorkspacePaymentInfo,
  IJobBoardDTO,
  ComposableDashboardFiltersEnum,
  IComposableAnalyticsGlobalFilters,
  getRecruiterActivitLogs,
  ActivityLogDTO,
  getRecruiterByEmail,
  getUserByEmail,
  getOverlapTeams,
} from 'wisestep-api';
import {
  formatDateInDayMonthYearTime,
  getFormatDate,
  IUserDTO,
  lightTokens,
  ScrollableButtonTabs,
  WsColors,
  WsIconText,
  WsTimeLine,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import AccessControl from '../../../../components/AccessControl/AccessControl';
import { NotesParent } from '../../../../components/communicationBar/Notes';
import PinDashboardCollectionButton from '../../../../components/pinDashboardCollectionButton';
import { WsBreadCrumbs } from '../../../../components/WsBreadCrumbs';
import { WsTabs } from '../../../../components/WsTabs';
import { useUserContext, useWorkspaceContext } from '../../../../context';
import { WsActivityProps } from '../../../../features/activityLog/ActivityHelper';
import ComposeNote from '../../../../features/clientDetails/components/notes/ComposeNote';
import {
  getDashBoardBreadCrumbList,
  IDashBoardBreadCrumbTypes,
} from '../../../../features/users/helper/defaultValues';
import { useDrawer } from '../../../../hooks/useDrawer';
import { useFetch } from '../../../../hooks/useFetch';
import { useFetchDeps } from '../../../../hooks/useFetchDeps';
import CircleUserLock from '../../../../images/content/CircleUserLock.svg';
import { CRUCIBLE_WORKSPACE_ID, MAIL_ART_WORKSPACE_ID } from '../../../../utils/Constants';
import { failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import NotificationSettingsPage from '../../../notificationSettings';
import { StatusTypography } from '../../../teams/components/TeamMembersTable';
import { RecruiterAvatarGroupInfo } from '../RecruiterAvatarGroupInfo';
import { RecruiterInfo } from '../RecruiterInfo';
import RecruiterSettings from '../RecruiterSettings';
import SettingNewPassword from '../settingNewPassword/SettingNewPassword';

import { filteredTabNames } from './helper/profileHelper';
import UserProfile from './UserProfile';
import UserProfileOverview from './UserProfileOverview';
import UserProfileOverviewTemp from './UserProfileOverviewTemp';

const JOB_BOARD_NAME_MAP = {
  DICE_SEARCH: 'Dice',
  CAREER_BUILDER: 'Career Builder RDB',
  CAREER_BUILDER_EDGE: 'Career Builder Edge',
  TECH_FETCH: 'Tech Fetch',
  CV_LIBRARY: 'CV Library',
  MONSTER: 'Monster',
  MONSTER_US: 'Monster US',
  MONSTER_EU: 'Monster EU',
  JOB_SERVE: 'Job Serve',
  CW_JOBS: 'CW Jobs',
  JOBSITE: 'Job Site',
};

const darkTokens = {
  ...lightTokens,
  palette: { ...lightTokens.palette, background: { paper: WsColors.TheBlues900 }, mode: 'dark' },
} as ThemeOptions;

const Profile = () => {
  const location: any = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const loggedInUser = useUserContext().userDTO ?? null;
  const [params] = useSearchParams();
  const recruiterEmail = String(params.get('email') ?? '');
  const isLink: boolean = Boolean(params.get('isLink')) || false;
  const isUserAnalytics: boolean = Boolean(params.get('isUserAnalytics')) || false;
  const [recruiterId, setRecruiterId] = useState<string>('');
  const [isOverLap, setIsOverLap] = useState<string>('');
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [activityLogData, setActivityLogData] = useState<ActivityLogDTO[]>([]);
  const [activityLists, setActivityLists] = useState<WsActivityProps[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const userId = isLink ? recruiterId : queryParams.get('id') ?? loggedInUser?.id;
  const [id, setId] = useState<string | undefined>(userId);
  const [user, setUser] = useFetchDeps<IUserDTO | null>(
    ([id]) => (isUserAnalytics ? getRecruiterByEmail(recruiterEmail) : getUserById(id)),
    [id],
    null
  );
  const [workspacePaymentInfo] = useFetchDeps<IWorkspacePaymentInfo | null>(
    () => getWorkspacePaymentInfo(),
    [user],
    null
  );
  const [workspaceUser] = useFetchDeps<IWorkspace>(
    ([user]) => getWorkspaceUserById(user?.email),
    [user]
  );
  const [openDrawer, closeDrawer] = useDrawer();
  const [smsAccounts] = useFetchDeps<ICommunicationServiceAccount[]>(
    ([user]) => getCommunicationAccountsForRecruiter(user?.email),
    [user]
  );
  const [jobBoards] = useFetchDeps<IJobBoardDTO[]>(
    () => getJobBoardByJBName('all', user?.email as string),
    [user]
  );
  const [emailAccounts] = useFetchDeps<ISMTPAccountsResponse[]>(
    ([user]) => getSMTPAccountsByRecruiter(user?.email),
    [user]
  );
  const [outlookCalendarAccounts] = useFetchDeps<ICalendarDTO[]>(
    ([user]) => getOutlookCalendarAccountsForRecruiter(user?.email),
    [user]
  );
  const [googleCalendarAccounts] = useFetchDeps<ICalendarDTO[]>(
    ([user]) => getGoogleCalendarAccountsForRecruiter(user?.email),
    [user]
  );
  const [clients] = useFetchDeps(() => getParentList(), [user], []);
  const [selectedOption, setSelectedOption] = useState(0);
  const [jobPostingBoards] = useFetch<IJobBoard[]>(() =>
    getJobBoardsByName('Monster,Dice,TechFetch,CareerBuilder,CWJobs')
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = (await getUserByEmail(recruiterEmail))?.data;
        const currentUserTeams = await getOverlapTeams(recruiterEmail);
        const hasOverlap = currentUserTeams.data?.length > 0 ? true : false;
        user && setRecruiterId(user.id);
        setId(user.id);
        setIsOverLap(hasOverlap ? 'yes' : 'no');
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [recruiterEmail]);

  const phoneNumbers = smsAccounts?.flatMap((s) => s?.phoneNumbers?.map((p) => p.phoneNumber));

  const getFilters = useMemo(() => {
    const filters = {} as Record<ComposableDashboardFiltersEnum, IComposableAnalyticsGlobalFilters>;
    filters[ComposableDashboardFiltersEnum.RECRUITER] = {
      recruiterNames: [user?.email ?? ''],
    };
    return filters;
  }, [user?.email]);
  const workspaceContext = useWorkspaceContext();
  const workspaceForNewProfileTaggingFlow = [
    CRUCIBLE_WORKSPACE_ID,
    MAIL_ART_WORKSPACE_ID,
    'c809aa49-c696-4ea1-8467-0108a16ab786',
  ];

  useEffect(() => {
    const fetchDataLog = async () => {
      try {
        if (!user?.email) {
          setActivityLogData([]);
          return;
        }
        const { data: activityResponseEntity } = await getRecruiterActivitLogs(user?.email ?? '');
        setActivityLogData(activityResponseEntity);
        const updatedActivityLists = activityResponseEntity.map((data) => ({
          activity: (
            <TimelineItem key={data.id}>
              <Grid container>
                <Grid item xs={12}>
                  <WsTypography color={WsTypoColors.TERTIARY}>
                    {` At ${formatDateInDayMonthYearTime(data.createdOn.toString())}`}
                  </WsTypography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant={WsTypoVariants.SUB_TITLE}>
                    {`${data.field} Category -> ${data.description} client ${data.parentEntitiyName}`}
                  </Typography>
                </Grid>
              </Grid>
            </TimelineItem>
          ),
          leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize="small" />,
        }));

        setActivityLists(updatedActivityLists);
      } catch {
        setNotification(failed(`Failed to fetch company Information`));
      }
    };
    fetchDataLog();
  }, [setNotification, user?.email]);

  return (
    <>
      {isUserAnalytics && (
        <WsBreadCrumbs
          breadCrumbsList={getDashBoardBreadCrumbList(
            IDashBoardBreadCrumbTypes.USERS,
            user?.firstName || user?.lastName ? `${user?.firstName} ${user?.lastName}` : 'User'
          )}
        />
      )}
      <ThemeProvider theme={() => createTheme(darkTokens)}>
        <Card sx={{ borderRadius: 4, p: '1rem 2rem 2rem 1rem' }}>
          <Grid container justifyContent="space-between">
            <Grid display="flex" flexDirection="row" alignItems="center" columnGap={1} ml={6}>
              <WsTypography
                variant={WsTypoVariants.TITLE}
                color={WsTypoColors.WHITE}
                cursor="pointer"
              >
                {user?.firstName} {user?.lastName}
              </WsTypography>
              {user?.linkedInProfileLink && (
                <IconButton
                  onClick={() => window.open(user?.linkedInProfileLink ?? undefined, '_blank')}
                >
                  <LinkedInIcon />
                </IconButton>
              )}
              <Chip
                label={workspaceUser?.role ?? ''}
                color="primary"
                sx={{
                  color: 'common.white',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  fontSize: 14,
                }}
                size="small"
              />
            </Grid>
            <Stack direction="row" spacing={1} alignItems="center">
              {user?.email && (
                <PinDashboardCollectionButton
                  filters={getFilters}
                  entityId={user?.email}
                  entityName={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`?.trim()}
                  fromProfile={true}
                />
              )}
              <StatusTypography
                cell={
                  {
                    value: workspaceUser?.barred
                      ? IInviteStatus.DEACTIVATED
                      : workspaceUser?.accepted
                      ? IInviteStatus.ACTIVATED
                      : IInviteStatus.INVITED,
                  } as Cell
                }
              >
                {workspaceUser?.barred
                  ? IInviteStatus.DEACTIVATED
                  : workspaceUser?.accepted
                  ? IInviteStatus.ACTIVATED
                  : IInviteStatus.INVITED}
              </StatusTypography>
              {!isLink && (
                <Button
                  onClick={() =>
                    openDrawer({
                      children: (
                        <UserProfile
                          loggedInUser={user}
                          workspacePaymentInfo={workspacePaymentInfo}
                          onSave={() => {
                            getUserById(id ?? '').then((res) => setUser(res.data));
                            closeDrawer();
                          }}
                        />
                      ),
                    })
                  }
                  startIcon={<HiOutlineEye />}
                >
                  Edit Profile
                </Button>
              )}
            </Stack>
          </Grid>
          <Stack ml={6} spacing={2}>
            <WsTypography
              variant={WsTypoVariants.BODY2}
              color={WsTypoColors.WHITE}
              cursor="pointer"
            >
              Profile created on {getFormatDate(workspaceUser?.requestedOn?.toString() ?? '')}
            </WsTypography>
            <Stack direction="row" justifyContent="space-between" px={1} spacing={24}>
              <Grid container rowSpacing={3} py={2}>
                <Grid item xs={4}>
                  <WsIconText
                    icon={<MdMailOutline size={20} />}
                    text={user?.email ?? 'Not Available'}
                    textColor={WsTypoColors.WHITE}
                    textSize={WsTypoVariants.SUB_TITLE}
                    popOverText="Registered Email"
                    textCursor="pointer"
                  />
                </Grid>
                <Grid item xs={4}>
                  <WsIconText
                    icon={<MdOutlinePhone size={20} />}
                    text={user?.phoneNumber ?? 'Not Available'}
                    textColor={WsTypoColors.WHITE}
                    textSize={WsTypoVariants.SUB_TITLE}
                    popOverText="Registered Phone Number"
                    textCursor="pointer"
                  />
                </Grid>
                <Grid item xs={4}>
                  <WsIconText
                    icon={<MdOutlineLocationOn size={20} />}
                    text={
                      [user?.location?.city, user?.location?.state, user?.location?.country]
                        .filter(Boolean)
                        .join(',') || 'Not Available'
                    }
                    textColor={WsTypoColors.WHITE}
                    textSize={WsTypoVariants.SUB_TITLE}
                    popOverText="Location"
                    textCursor="pointer"
                  />
                </Grid>
                <RecruiterAvatarGroupInfo
                  title="Clients Allocated"
                  Icon={HiOutlineBriefcase}
                  list={clients?.map(({ name }) => ({ name }))}
                />
                <RecruiterInfo
                  title="SMS Numbers Allocated"
                  Icon={SmsOutlinedIcon}
                  list={phoneNumbers}
                />
                <RecruiterInfo
                  title="Emails Configured"
                  Icon={AttachEmailOutlinedIcon}
                  list={emailAccounts?.map((e) => e.fromEmail)}
                />
                <RecruiterAvatarGroupInfo
                  title="Job Board Search Accounts"
                  Icon={MdOutlineScreenSearchDesktop}
                  list={jobBoards
                    ?.filter((jobBoard) =>
                      jobBoard.jobBoardSearchAccountUsers
                        ?.map(({ email }) => email)
                        .includes(user?.email as string)
                    )
                    .map(({ jobBoardName }) => ({
                      name: JOB_BOARD_NAME_MAP[jobBoardName] ?? jobBoardName,
                    }))}
                />
                <RecruiterAvatarGroupInfo
                  title="Job Publishing Accounts"
                  Icon={MdOutlineCampaign}
                  list={jobPostingBoards
                    ?.filter((jobBoard) =>
                      jobBoard.jobBoardSharedUsers
                        ?.map(({ email }) => email)
                        .includes(user?.email as string)
                    )
                    .map(({ jobBoardName }) => ({ name: jobBoardName }))}
                />
                <RecruiterInfo
                  title="Calendar Accounts Configured"
                  Icon={CalendarMonthOutlinedIcon}
                  list={[...(outlookCalendarAccounts || []), ...(googleCalendarAccounts || [])].map(
                    (c) => c.email
                  )}
                />
              </Grid>
              <ThemeProvider
                theme={(theme) => createTheme({ ...theme, palette: { mode: 'light' } })}
              >
                <Avatar sx={{ width: 96, height: 96 }}>
                  <PersonIcon sx={{ fontSize: 48 }} />
                </Avatar>
              </ThemeProvider>
            </Stack>
          </Stack>
        </Card>
      </ThemeProvider>
      <Grid item borderRadius={2} sx={{ background: WsColors.White }} mt={2} mb={1}>
        <ScrollableButtonTabs
          scrollList={filteredTabNames(isLink)}
          getTabIndex={(index: number) => setTabIndex(index)}
          defaultValue={tabIndex}
        />
      </Grid>
      {tabIndex == 0 && (
        <>
          {isOverLap == 'yes' ? (
            workspaceForNewProfileTaggingFlow.includes(workspaceContext?.id as string) ? (
              <UserProfileOverview
                recruiterId={!isUserAnalytics ? user?.email : undefined}
                userId={isUserAnalytics ? recruiterEmail : undefined}
              />
            ) : (
              <UserProfileOverviewTemp userId={isUserAnalytics ? recruiterEmail : undefined} />
            )
          ) : (
            <Grid item xs={8.5}>
              <AccessControl
                errorText="You cannot access recruiters data as the user is not part of any mutual teams"
                icon={<img src={CircleUserLock} alt="No Data Available" />}
                hideNavigation={true}
              />
            </Grid>
          )}
        </>
      )}
      {tabIndex == 1 && !isLink && (
        <Grid container rowGap={3}>
          <Grid item xs={3}>
            <RecruiterSettings getSelectedOption={setSelectedOption} />
          </Grid>
          <Grid item xs={9} pt={3}>
            {selectedOption === 0 ? <NotificationSettingsPage /> : <SettingNewPassword />}
          </Grid>
        </Grid>
      )}
      {(isLink ? tabIndex == 1 : tabIndex == 2) && (
        <WsTabs>
          <WsTabs.Item label="Notes">
            <ComposeNote
              parent={NotesParent.USERPROFILE}
              parentId={''}
              isClient={true}
              email={user?.email ?? ''}
            />
          </WsTabs.Item>
          <WsTabs.Item label="Activities">
            {activityLogData && (
              <Grid item xs={12} borderRadius={2} pl={2} sx={{ background: WsColors.White }}>
                <Timeline>
                  <Box ml="-100%">
                    <WsTimeLine activityList={activityLists} timeLineDotVariant={'outlined'} />
                  </Box>
                </Timeline>
              </Grid>
            )}
          </WsTabs.Item>
        </WsTabs>
      )}
    </>
  );
};

export default Profile;
